import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  createLabelInfoModel,
  listArtFiles,
  listValidDeviceSkus,
  previewNewModel,
  type ArtFileRow,
  type ProcessedModelDetails,
} from "@/lib/artAndLabelsApi";

const HEADER_CELL =
  "sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-2 py-2 text-xs font-medium uppercase tracking-wide text-slate-600 backdrop-blur-sm";

function RulesViewer({ rules }: { rules: unknown }) {
  const text = JSON.stringify(rules ?? {}, null, 2);
  return (
    <pre className="max-h-[24rem] overflow-auto rounded-md border border-slate-100 bg-slate-50 p-4 text-xs text-slate-800">
      {text}
    </pre>
  );
}

export function ArtFilesPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modelName, setModelName] = useState("");
  const [sku, setSku] = useState("");
  const [deviceLabelName, setDeviceLabelName] = useState("");
  const [details, setDetails] = useState<ProcessedModelDetails | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const artFilesQuery = useQuery({
    queryKey: ["art-and-labels", "art-files"],
    queryFn: listArtFiles,
  });
  const deviceSkusQuery = useQuery({
    queryKey: ["art-and-labels", "models", "device-skus"],
    queryFn: listValidDeviceSkus,
    enabled: modalOpen,
  });

  const rows = artFilesQuery.data ?? [];
  const selectedRow = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null;

  useEffect(() => {
    if (selectedId == null && rows.length > 0) {
      setSelectedId(rows[0]!.id);
    }
  }, [rows, selectedId]);

  useEffect(() => {
    if (!modalOpen || !modelName.trim()) {
      setDetails(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void previewNewModel(modelName.trim().toUpperCase())
        .then((next) => {
          setDetails(next);
          if (next.deviceLabelName) {
            setDeviceLabelName(next.deviceLabelName);
          }
          setErrorMessage(null);
        })
        .catch((error: Error) => setErrorMessage(error.message));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [modalOpen, modelName]);

  const createMutation = useMutation({
    mutationFn: createLabelInfoModel,
    onSuccess: async () => {
      setStatusMessage("Model created.");
      setErrorMessage(null);
      setModalOpen(false);
      setModelName("");
      setSku("");
      setDeviceLabelName("");
      setDetails(null);
      await queryClient.invalidateQueries({ queryKey: ["art-and-labels"] });
    },
    onError: (error: Error) => setErrorMessage(error.message),
  });

  function resetModal() {
    setModelName("");
    setSku("");
    setDeviceLabelName("");
    setDetails(null);
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Browse art files and their CSV rules. Create label_info model records with auto-derived barcode and label names.">
        Art files
      </PageTitleWithHelp>

      {statusMessage ? <p className="text-sm text-emerald-700">{statusMessage}</p> : null}
      {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}

      <HubViewportCard>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Art files</CardTitle>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              resetModal();
              setModalOpen(true);
            }}
          >
            Create model
          </Button>
        </CardHeader>
        <CardBody className="grid min-h-0 flex-1 gap-6 p-0 lg:grid-cols-2">
          <div className="overflow-auto px-5 pb-5">
            {artFilesQuery.isLoading ? <p className="text-sm text-slate-600">Loading…</p> : null}
            {!artFilesQuery.isLoading ? (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={cn(HEADER_CELL, "text-left")}>Name</th>
                    <th className={cn(HEADER_CELL, "text-left")}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: ArtFileRow) => (
                    <tr
                      key={row.id}
                      className={cn(
                        "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                        selectedRow?.id === row.id && "bg-brand-50",
                      )}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td className="px-2 py-2 font-medium">{row.name}</td>
                      <td className="px-2 py-2">{row.label_type ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
          <div className="border-t border-slate-100 px-5 pb-5 lg:border-l lg:border-t-0">
            <h3 className="py-4 text-sm font-semibold text-slate-900">Rules</h3>
            {selectedRow ? <RulesViewer rules={selectedRow.rules} /> : <p className="text-sm text-slate-600">Select an art file.</p>}
          </div>
        </CardBody>
      </HubViewportCard>

      {modalOpen ? (
        <DocumentModal
          title="Create label model"
          onClose={() => {
            setModalOpen(false);
            resetModal();
          }}
          showCloseButton={false}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-700">Model</span>
              <input
                className="w-full rounded border border-slate-200 px-3 py-2 uppercase"
                value={modelName}
                onChange={(event) => setModelName(event.target.value.toUpperCase())}
                placeholder="FI-3C S04BCM"
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="mb-1 block font-medium text-slate-700">SKU</span>
              <select
                className="w-full rounded border border-slate-200 px-3 py-2"
                value={sku}
                onChange={(event) => setSku(event.target.value)}
              >
                <option value="">Select device SKU</option>
                {(deviceSkusQuery.data ?? []).map((deviceSku) => (
                  <option key={deviceSku} value={deviceSku}>
                    {deviceSku}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Product line</span>
              <input className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2" value={details?.productLine ?? ""} readOnly />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Product name</span>
              <input className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2" value={details?.productName ?? ""} readOnly />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Base config</span>
              <input className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2" value={details?.baseConfig ?? ""} readOnly />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Barcode model</span>
              <input className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2" value={details?.barCodeModel ?? ""} readOnly />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Box label name</span>
              <input className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2" value={details?.modelBoxName ?? ""} readOnly />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Device label name</span>
              <input
                className="w-full rounded border border-slate-200 px-3 py-2"
                value={deviceLabelName}
                onChange={(event) => setDeviceLabelName(event.target.value)}
              />
            </label>
            {details?.alreadyExists ? (
              <p className="text-sm text-amber-700 sm:col-span-2">This model already exists in label_info.</p>
            ) : null}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setModalOpen(false);
                resetModal();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={
                !modelName.trim() ||
                !sku ||
                !details ||
                details.alreadyExists ||
                !details.barCodeModel ||
                !details.modelBoxName ||
                !details.boxBaseArtName ||
                !deviceLabelName.trim() ||
                createMutation.isPending
              }
              onClick={() =>
                createMutation.mutate({
                  model: modelName.trim().toUpperCase(),
                  barCodeModel: details!.barCodeModel!,
                  boxBaseArtName: details!.boxBaseArtName!,
                  sku,
                  modelBox: details!.modelBoxName!,
                  modelDevice: deviceLabelName.trim(),
                })
              }
            >
              {createMutation.isPending ? "Saving…" : "Create model"}
            </Button>
          </div>
        </DocumentModal>
      ) : null}
    </div>
  );
}
